import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { NotificationTimeAnswer } from './TeamsCohortOnboarding';
import { H3, P, PExtraSmall, PSmall } from '../../components/elements/Text';
import { ColumnQuestionWrapper } from '../../components/Onboarding/Typeform/common';
import { Dropdown } from '../../components/elements/Dropdown';
import { Input } from '../../components/elements/Input';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export default function TimezoneTimeQuestion({
  id,
  text,
  subtext,
  answer,
  onAnswerQuestion,
  onNext,
}: {
  id: string;
  text: string;
  subtext?: string;
  answer: NotificationTimeAnswer | undefined;
  onAnswerQuestion: (id: string, answer: NotificationTimeAnswer) => void;
  onNext: () => void;
}) {
  const timezoneOptions = React.useMemo(
    () =>
      Intl.supportedValuesOf('timeZone').map((timezone) => ({
        value: timezone,
        label: timezone.replace(/_/g, ' '),
        utcOffset: dayjs().tz(timezone).utcOffset(),
      })),
    []
  );
  const [currentTimezone, setCurrentTimezone] = React.useState(
    timezoneOptions.find((option) => option.value === dayjs.tz.guess()) ??
      timezoneOptions[0]
  );
  const [currentTime, setCurrentTime] = React.useState('');

  React.useEffect(() => {
    if (
      currentTimezone &&
      currentTime &&
      answer?.timezone !== currentTimezone.value &&
      answer?.time !== currentTime
    ) {
      onAnswerQuestion(id, {
        timezone: currentTimezone.value,
        time: currentTime,
      });
    }
  }, [currentTimezone, currentTime, onAnswerQuestion, id, answer]);

  console.log(currentTime);
  return (
    <ColumnQuestionWrapper>
      <H3>{text}</H3>
      {subtext && <P>{subtext}</P>}
      <P>We selected your timezone for you, but feel free to change it.</P>
      <PSmall>Timezone</PSmall>
      <Dropdown
        options={timezoneOptions}
        value={currentTimezone}
        onChange={(option) => {
          setCurrentTimezone(option);
        }}
      />
      <PSmall>Notification Time</PSmall>
      <Input
        style={{ marginTop: 8 }}
        type="time"
        autoFocus
        value={currentTime}
        onChange={(e) => {
          const timeValue = e.target.value;

          // Remove rounding logic from onChange
          // For all other cases, just set the value as is
          setCurrentTime(timeValue);
        }}
        shouldSubmitOnEnter
        onSubmit={() => {
          if (currentTime.includes(':') && currentTime.length === 5) {
            const [hours, minutes] = currentTime.split(':').map(Number);

            if (!isNaN(minutes)) {
              const roundedMinutes = Math.round(minutes / 30) * 30;
              const adjustedHours = hours + Math.floor(roundedMinutes / 60);
              const finalMinutes = roundedMinutes % 60;

              const paddedHours = adjustedHours.toString().padStart(2, '0');
              const paddedMinutes = finalMinutes.toString().padStart(2, '0');

              const roundedTime = `${paddedHours}:${paddedMinutes}`;
              setCurrentTime(roundedTime);
              onAnswerQuestion(id, {
                timezone: currentTimezone.value,
                time: roundedTime,
              });
            }
          }
          onNext();
        }}
        canSubmit={!!currentTime && !!currentTimezone}
        onBlur={() => {
          if (currentTime.includes(':') && currentTime.length === 5) {
            const [hours, minutes] = currentTime.split(':').map(Number);

            if (!isNaN(minutes)) {
              const roundedMinutes = Math.round(minutes / 30) * 30;
              const adjustedHours = hours + Math.floor(roundedMinutes / 60);
              const finalMinutes = roundedMinutes % 60;

              const paddedHours = adjustedHours.toString().padStart(2, '0');
              const paddedMinutes = finalMinutes.toString().padStart(2, '0');

              setCurrentTime(`${paddedHours}:${paddedMinutes}`);
              onAnswerQuestion(id, {
                timezone: currentTimezone.value,
                time: `${paddedHours}:${paddedMinutes}`,
              });
            }
          }
        }}
      />
      <PExtraSmall style={{ marginTop: 8 }}>
        PS: we'll round this to the nearest 30 minutes
      </PExtraSmall>
    </ColumnQuestionWrapper>
  );
}
